import {
  getSession,
  Session,
  type Session as AnalyticsSession,
} from "tk-buyer";
import {
  OptionsType,
  OptionInteractionType,
  type Configuration,
} from "@threekit/rest-api";
import {
  scheduleApplyCPQSelections,
  type ApplyCPQSelectionsOptions,
  type ApplyCPQSelectionsResult,
  runApplyCPQSelections,
} from "./cpq-apply-selections";
import {
  optionsShowAllFromConfigurator as optionsShowAllFromConfiguratorCore,
  type OptionsShowAllFromConfiguratorOptions,
} from "./options-show-all-attributes";
import {
  getCpqAttributeKeyFromDisplayAttribute,
  listCpqLinkedDisplayAttributes,
  listCpqLinkedDisplayAttributesFromConfigurator,
  type CpqLinkedDisplayAttribute,
} from "./configurator-attribute-utils";
import {
  snapshotAndUploadConfiguration as snapshotAndUploadConfigurationCore,
  snapshotCamerasAndUpload as snapshotCamerasAndUploadCore,
  snapshotStandardCamerasAndUpload as snapshotStandardCamerasAndUploadCore,
  resolveCameraId as resolveCameraIdCore,
  type SnapshotAndUploadOptions,
  type SnapshotAndUploadResult,
  type CameraSnapshotSpec,
  type SnapshotCamerasResult,
  type SnapshotStandardCamerasResult,
} from "./snapshot-upload";
import {
  registerCpqLinkedAttributeListeners as registerCpqLinkedAttributeListenersCore,
  type RegisterCpqLinkedAttributeListenersOptions,
  type CpqLinkedAttributeListenersHandle,
} from "./cpq-linked-attribute-listeners";
import {
  generateArQrCodeAndUpload as generateArQrCodeAndUploadCore,
  type ArQrCodeUploadOptions,
  type ArQrCodeUploadResult,
} from "./ar-qr-upload";

export type { OptionsShowAllFromConfiguratorOptions };
export type {
  SnapshotAndUploadOptions,
  SnapshotAndUploadResult,
  ThreekitSnapshotAsyncOptions,
  CameraSnapshotSpec,
  SnapshotCamerasResult,
  SnapshotStandardCamerasResult,
  StandardSnapshotCameraKey,
} from "./snapshot-upload";
export { STANDARD_SNAPSHOT_CAMERAS } from "./snapshot-upload";
export { threekitFilesApiUrl, threekitFileContentUrl } from "./snapshot-upload";
export type { CpqLinkedDisplayAttribute };
export {
  getCpqAttributeKeyFromDisplayAttribute,
  listCpqLinkedDisplayAttributes,
  listCpqLinkedDisplayAttributesFromConfigurator,
};
export type { ApplyCPQSelectionsOptions, ApplyCPQSelectionsResult };
export { runApplyCPQSelections };
export {
  registerCpqLinkedAttributeListeners,
  type CpqJsAttributeApi,
  type RegisterCpqLinkedAttributeListenersOptions,
  type CpqLinkedAttributeListenersHandle,
} from "./cpq-linked-attribute-listeners";
export type { ArQrCodeUploadOptions, ArQrCodeUploadResult } from "./ar-qr-upload";

export type ThreekitEnvHost = `${string}.threekit.com` | string;

export interface ThreekitPlayerInitOptions {
  /** Subdomain host, e.g. `preview` → `https://preview.threekit.com/...` */
  env: string;
  authToken: string;
  assetId?: string;
  customId?: string;
  stageId?: string;
  branch?: string;
  publishStage?: "published" | "draft";
  display?: "webgl" | "image";
  showConfigurator?: boolean;
  showAR?: boolean;
  showShare?: boolean;
  locale?: string;
  analyticsCustomId?: string;
  /**
   * Applied at embed time. Omit entirely if you do not want a default configuration.
   * See https://developer.threekit.com/reference/embedding-the-threekit-player
   */
  configuration?: unknown;
  initialConfiguration?: unknown;
  configurationId?: string;
  /**
   * Same-origin path for snapshot uploads (e.g. `/api/threekit-files`). When set, the browser
   * POSTs multipart `files` here; the server must forward to Threekit with `THREEKIT_API_TOKEN`.
   */
  filesUploadProxyPath?: string;
  /**
   * Same-origin path (or absolute https URL, for direct CPQ embed) for
   * `handle.generateArQrCodeAndUpload()`. Default `/api/ar/qrUrl`.
   */
  arQrUrlEndpoint?: string;
}

export interface AnalyticsInitOptions {
  orgId: string;
  publicToken: string;
  /** e.g. `admin-fts.threekit.com` per https://developer.threekit.com/reference/analytics-sdk */
  host: string;
  /** Console logging for analytics (recommended in development) */
  log?: boolean;
}

export interface EmbedInitOptions {
  /** CSS selector or element for the player mount */
  container: string | HTMLElement;
  threekit: ThreekitPlayerInitOptions;
  analytics: AnalyticsInitOptions;
}

export type OptionsShowParams = Parameters<AnalyticsSession["optionsShow"]>[0];
export type OptionInteractionParams = Parameters<
  AnalyticsSession["optionInteraction"]
>[0];
export type CustomEventParams = Parameters<AnalyticsSession["custom"]>[0];

export interface ThreekitEmbedHandle {
  /** Underlying player instance from `threekitPlayer()` */
  player: Awaited<ReturnType<NonNullable<Window["threekitPlayer"]>>>;
  configurator: Awaited<
    ReturnType<
      Awaited<
        ReturnType<NonNullable<Window["threekitPlayer"]>>
      >["getConfigurator"]
    >
  >;
  /** @threekit/analytics session */
  analytics: AnalyticsSession;
  /**
   * Sets one or more attribute values. Always await before follow-up work.
   * https://developer.threekit.com/reference/configurator-api#set-configuration
   */
  setConfiguration(configuration: unknown): Promise<unknown>;
  /** https://developer.threekit.com/reference/analytics-sdk#options-show */
  optionsShow(params: OptionsShowParams): void;
  /** https://developer.threekit.com/reference/analytics-sdk#options-interaction */
  optionInteraction(params: OptionInteractionParams): void;
  /** https://developer.threekit.com/reference/analytics-sdk#custom-events */
  custom(params: CustomEventParams): void;
  /** Re-exported enums for convenience */
  OptionsType: typeof OptionsType;
  OptionInteractionType: typeof OptionInteractionType;
  /**
   * Loads attributes via the Configurator API (`getAttributes` or `getDisplayAttributes`),
   * then fires `optionsShow` for each attribute’s option list.
   * https://developer.threekit.com/reference/configurator-api
   */
  optionsShowAllFromConfigurator(opts?: OptionsShowAllFromConfiguratorOptions): void;
  /** Catalog item id used at embed time; used as default `parentAssetId` for optionsShow. */
  productAssetId?: string;
  /** Threekit env subdomain the player was mounted with (e.g. `preview`, `admin-fts`). */
  threekitEnv: string;
  /**
   * Org id the player was mounted with. With `threekitEnv` it identifies the environment
   * (dev / staging / prod) for snapshot uploads and AR links; staging and prod share `admin-fts`.
   */
  orgId: string;
  /**
   * Map Oracle CPQ keys to Threekit via `metadata.cpq_attribute` (or `metadata.cpq_attributeAlt`)
   * / option `metadata.cpq_value` (or, for `Boolean` attributes, `metadata.cpq_true_value`),
   * then `setConfiguration` + buyer analytics. Returns immediately; use `onComplete` for results.
   */
  applyCPQSelections(
    selections: Record<string, string | number>,
    options?: ApplyCPQSelectionsOptions
  ): void;
  /**
   * Display attributes that declare `metadata.cpq_attribute`, for reading current values from Oracle
   * CPQ (`CPQJS.getAttributeVal`) and passing them to `applyCPQSelections`. Rows also carry
   * `cpqAttributeAlt` when the attribute declares `metadata.cpq_attributeAlt` — the alternate CPQ
   * field name used for the same Threekit attribute when a different model is selected.
   */
  getCpqLinkedDisplayAttributes(): CpqLinkedDisplayAttribute[];
  /**
   * Register `CPQJS.onAttributeChange` for each linked `cpq_attribute` (and `cpq_attributeAlt`,
   * when present) and push changes into Threekit via {@link applyCPQSelections}. Same-window /
   * direct-embed only (needs `CPQJS`).
   */
  registerCpqLinkedAttributeListeners(
    options?: RegisterCpqLinkedAttributeListenersOptions
  ): CpqLinkedAttributeListenersHandle;
  /**
   * Snapshot the current player ([docs](https://developer.threekit.com/reference/snapshot)),
   * upload via Files API ([docs](https://developer.threekit.com/reference/uploadfiles)),
   * and return an **https** URL you can persist in CPQ.
   */
  snapshotAndUploadConfiguration(
    options?: SnapshotAndUploadOptions
  ): Promise<SnapshotAndUploadResult>;
  /**
   * Snapshots and uploads once per named camera, resolving each name to a scene node id via
   * `player.scene.findNode({ name })` ([docs](https://developer.threekit.com/reference/scene)),
   * then calling {@link snapshotAndUploadConfiguration} with that node's id as `cameraId`.
   */
  snapshotCamerasAndUpload(
    cameras: CameraSnapshotSpec[],
    options?: SnapshotAndUploadOptions
  ): Promise<SnapshotCamerasResult>;
  /**
   * {@link snapshotCamerasAndUpload} with this product's 3 cameras hardcoded
   * (`STANDARD_SNAPSHOT_CAMERAS`: `main`, `front`, `side`) — no camera names to pass from CPQ.
   */
  snapshotStandardCamerasAndUpload(
    options?: SnapshotAndUploadOptions
  ): Promise<SnapshotStandardCamerasResult>;
  /**
   * Resolves a scene camera node's id by name via
   * `player.scene.findNode({ name, hierarchical: true })`
   * ([docs](https://developer.threekit.com/reference/scene)) — e.g. to pass as
   * `options.snapshot.cameraId` to {@link snapshotAndUploadConfiguration}. Throws if no node
   * with that name exists.
   */
  resolveCameraId(cameraName: string): string;
  /**
   * Saves the current configuration and asks the server to generate the AR QR code and upload
   * it to Threekit's Files API (see `server/ar-service.mjs`, `GET /api/ar/qrUrl`), returning a
   * stable https URL you can persist in CPQ (e.g. `CPQJS.setAttributeVal(...)`) and reuse later
   * without regenerating the QR code on demand.
   */
  generateArQrCodeAndUpload(
    options?: ArQrCodeUploadOptions
  ): Promise<ArQrCodeUploadResult>;
  destroy(): void;
}

const SCRIPT_PATH = "/app/js/threekit-player.js";

function resolveContainer(container: string | HTMLElement): HTMLElement {
  if (typeof container === "string") {
    const el = document.querySelector(container);
    if (!el) throw new Error(`ThreekitEmbed: container not found: ${container}`);
    return el as HTMLElement;
  }
  return container;
}

function loadThreekitPlayerScript(
  env: string,
  bearerToken: string
): Promise<void> {
  const src = `https://${env}.threekit.com${SCRIPT_PATH}?bearer_token=${encodeURIComponent(bearerToken)}`;
  return new Promise((resolvePromise, reject) => {
    if (typeof window.threekitPlayer === "function") {
      resolvePromise();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-threekit-player="${env}"]`
    );
    if (existing) {
      existing.addEventListener("load", () => resolvePromise());
      existing.addEventListener("error", () =>
        reject(new Error("ThreekitEmbed: failed to load threekit-player.js"))
      );
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.threekitPlayer = env;
    script.onload = () => resolvePromise();
    script.onerror = () =>
      reject(new Error(`ThreekitEmbed: could not load ${src}`));
    document.head.appendChild(script);
  });
}

function buildPlayerOptions(
  el: HTMLElement,
  tk: ThreekitPlayerInitOptions
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    el,
    authToken: tk.authToken,
    publishStage: tk.publishStage ?? "published",
    showConfigurator: tk.showConfigurator ?? true,
  };

  if (tk.assetId) base.assetId = tk.assetId;
  if (tk.customId) base.customId = tk.customId;
  if (tk.stageId) base.stageId = tk.stageId;
  if (tk.branch) base.branch = tk.branch;
  if (tk.display) base.display = tk.display;
  if (tk.showAR !== undefined) base.showAR = tk.showAR;
  if (tk.showShare !== undefined) base.showShare = tk.showShare;
  if (tk.locale) base.locale = tk.locale;
  if (tk.analyticsCustomId) base.analyticsCustomId = tk.analyticsCustomId;

  if (tk.configuration !== undefined) base.configuration = tk.configuration;
  if (tk.initialConfiguration !== undefined)
    base.initialConfiguration = tk.initialConfiguration;
  if (tk.configurationId !== undefined) base.configurationId = tk.configurationId;

  return base;
}

/**
 * Initialize the Threekit player and analytics session. Call once per embed.
 * `authToken`, `orgId`, and analytics `publicToken` must be supplied by the host app;
 * this library does not read `.env` or other app configuration.
 */
export async function init(options: EmbedInitOptions): Promise<ThreekitEmbedHandle> {
  const el = resolveContainer(options.container);
  const { env, authToken } = options.threekit;
  const threekitEnv = env;
  const threekitAuthToken = authToken;
  const threekitFilesUploadProxyPath =
    options.threekit.filesUploadProxyPath?.trim() || null;
  const threekitArQrUrlEndpoint = options.threekit.arQrUrlEndpoint?.trim() || null;
  const threekitOrgId = options.analytics.orgId;

  await loadThreekitPlayerScript(env, authToken);

  const threekitPlayerFn = window.threekitPlayer;
  if (typeof threekitPlayerFn !== "function") {
    throw new Error("ThreekitEmbed: window.threekitPlayer is not available");
  }

  const playerOpts = buildPlayerOptions(el, options.threekit);
  const player = await threekitPlayerFn(
    playerOpts as Parameters<typeof threekitPlayerFn>[0]
  );
  const configurator = await player.getConfigurator();

  const { orgId, publicToken, host, log = false } = options.analytics;
  Session.log = log;
  const session = getSession({
    auth: { orgId, publicToken, host },
  });

  const handle: ThreekitEmbedHandle = {
    player,
    configurator,
    analytics: session,
    productAssetId: options.threekit.assetId,
    threekitEnv,
    orgId: threekitOrgId,
    OptionsType,
    OptionInteractionType,
    setConfiguration(configuration: unknown) {
      return configurator.setConfiguration(configuration);
    },
    optionsShow(params: OptionsShowParams) {
      session.optionsShow(params);
    },
    optionInteraction(params: OptionInteractionParams) {
      session.optionInteraction(params);
    },
    custom(params: CustomEventParams) {
      session.custom(params);
    },
    optionsShowAllFromConfigurator(opts?: OptionsShowAllFromConfiguratorOptions) {
      optionsShowAllFromConfiguratorCore(this, opts);
    },
    applyCPQSelections(
      selections: Record<string, string | number>,
      opts?: ApplyCPQSelectionsOptions
    ) {
      scheduleApplyCPQSelections(this, selections, opts);
    },
    getCpqLinkedDisplayAttributes() {
      return listCpqLinkedDisplayAttributesFromConfigurator(configurator);
    },
    registerCpqLinkedAttributeListeners(
      opts?: RegisterCpqLinkedAttributeListenersOptions
    ) {
      return registerCpqLinkedAttributeListenersCore(this, opts);
    },
    snapshotAndUploadConfiguration(snapshotOpts?: SnapshotAndUploadOptions) {
      return snapshotAndUploadConfigurationCore({
        env: threekitEnv,
        orgId: threekitOrgId,
        authToken: threekitAuthToken,
        player,
        options: snapshotOpts,
        filesUploadProxyPath: threekitFilesUploadProxyPath,
      });
    },
    snapshotCamerasAndUpload(
      cameras: CameraSnapshotSpec[],
      snapshotOpts?: SnapshotAndUploadOptions
    ) {
      return snapshotCamerasAndUploadCore({
        env: threekitEnv,
        orgId: threekitOrgId,
        authToken: threekitAuthToken,
        player,
        cameras,
        options: snapshotOpts,
        filesUploadProxyPath: threekitFilesUploadProxyPath,
      });
    },
    snapshotStandardCamerasAndUpload(snapshotOpts?: SnapshotAndUploadOptions) {
      return snapshotStandardCamerasAndUploadCore({
        env: threekitEnv,
        orgId: threekitOrgId,
        authToken: threekitAuthToken,
        player,
        options: snapshotOpts,
        filesUploadProxyPath: threekitFilesUploadProxyPath,
      });
    },
    resolveCameraId(cameraName: string) {
      return resolveCameraIdCore(player, cameraName);
    },
    generateArQrCodeAndUpload(arQrOpts?: ArQrCodeUploadOptions) {
      return generateArQrCodeAndUploadCore({
        env: threekitEnv,
        orgId: threekitOrgId,
        assetId: options.threekit.assetId ?? "",
        player,
        options: {
          qrUrlEndpoint: arQrOpts?.qrUrlEndpoint ?? threekitArQrUrlEndpoint ?? undefined,
          metadata: arQrOpts?.metadata,
          env: arQrOpts?.env,
          orgId: arQrOpts?.orgId,
        },
      });
    },
    destroy() {
      el.replaceChildren();
    },
  };

  return handle;
}

/**
 * Same as `handle.optionsShowAllFromConfigurator()` — for use without keeping the handle shape in scope.
 */
export function optionsShowAllFromConfigurator(
  handle: ThreekitEmbedHandle,
  opts?: OptionsShowAllFromConfiguratorOptions
): void {
  optionsShowAllFromConfiguratorCore(handle, opts);
}

/** Same as `handle.applyCPQSelections(...)` — for hosts that keep the handle in closure. */
export function applyCPQSelections(
  handle: ThreekitEmbedHandle,
  selections: Record<string, string | number>,
  opts?: ApplyCPQSelectionsOptions
): void {
  scheduleApplyCPQSelections(handle, selections, opts);
}

/**
 * Build the `selections` object for {@link applyCPQSelections} from CPQ current values.
 * `getValue` should return the raw CPQ field value for each `cpqAttribute` (e.g.
 * `CPQJS.getAttributeVal(name)` when `CPQJS.attributeExists(name)`).
 *
 * When a row has `cpqAttributeAlt` (a Threekit attribute mapped to a different CPQ field
 * depending on which model is selected), `getValue` is called for both `cpqAttribute` and
 * `cpqAttributeAlt` — pass a `getValue` that returns `undefined` for a field that doesn't exist
 * on the current model (e.g. via `CPQJS.attributeExists`) so only the one that's actually present
 * ends up in `selections`.
 *
 * An empty string is still included (not skipped) — it represents a multi-select CPQ field
 * with nothing checked, and `runApplyCPQSelections` resolves that to the Threekit attribute's
 * `cpq_value: "None"` option when the attribute is tagged `metadata.removeLeftRight`.
 */
export function buildCpqSelectionsForApply(
  linked: CpqLinkedDisplayAttribute[],
  getValue: (cpqAttribute: string) => unknown
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const row of linked) {
    const keys = [row.cpqAttribute, row.cpqAttributeAlt].filter(
      (k): k is string => typeof k === "string" && k.length > 0
    );
    for (const key of keys) {
      const raw = getValue(key);
      if (raw === undefined || raw === null) continue;
      if (typeof raw === "number" && Number.isFinite(raw)) {
        out[key] = raw;
        continue;
      }
      out[key] = String(raw).trim();
    }
  }
  return out;
}

/**
 * Fire `optionsShow` using the session from a prior `init()`.
 * https://developer.threekit.com/reference/analytics-sdk#options-show
 */
export function analyticsOptionsShow(
  session: AnalyticsSession,
  params: OptionsShowParams
): void {
  session.optionsShow(params);
}

/**
 * Fire `optionInteraction` (e.g. select).
 * https://developer.threekit.com/reference/analytics-sdk#options-interaction
 */
export function analyticsOptionInteraction(
  session: AnalyticsSession,
  params: OptionInteractionParams
): void {
  session.optionInteraction(params);
}

/**
 * Fire a named custom analytics event.
 * https://developer.threekit.com/reference/analytics-sdk#custom-events
 */
export function analyticsCustom(
  session: AnalyticsSession,
  params: CustomEventParams
): void {
  session.custom(params);
}

export type { Configuration };
export { OptionsType, OptionInteractionType };
